import {ApplicationData} from '@agenium-ai/agenium-client-sdk/dist/models';
import {useEffect, useMemo} from 'react';
import {useUnderwritingAnalytics} from './useUnderwritingAnalytics';

type ApplicationDecision = 'APPROVED' | 'REFERRED' | 'CANCELLED' | 'DECLINED' | null;

/**
 * Normalizes the raw `Case_UWDecision_Overall` value into a canonical decision.
 * Exported so callers outside React render (e.g. imperative submit handlers) can
 * re-check the decision against the same rules the `useUnderwritingDecision` hook uses.
 */
export const getUnderwritingDecision = (applicationData: ApplicationData[] | null): ApplicationDecision => {
    const decision = applicationData?.find((item) => item.key === 'Case_UWDecision_Overall')?.value;

    if (!decision) return null;

    if (decision.match(/\b(refer(red)?)\b/gi)) {
        return 'REFERRED';
    } else if (decision.toLowerCase().match(/postpone|decline(d?)/gi)) {
        return 'DECLINED';
    } else if (decision.toLowerCase().match(/withdrawn|ineligible|incomplete/gi)) {
        return 'CANCELLED';
    } else {
        return 'APPROVED';
    }
};

export const useUnderwritingDecision = (
    applicationData: ApplicationData[] | null,
    applicationId: string,
    formId: string,
): ApplicationDecision => {
    const {storeDecision} = useUnderwritingAnalytics({
        applicationId: applicationId,
        formId: formId,
    });

    const decision = useMemo(() => getUnderwritingDecision(applicationData), [applicationData]);

    useEffect(() => {
        if (decision && formId) {
            storeDecision({decision});
        }
    }, [decision, formId, storeDecision]);

    return decision;
};
